
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { translateCategoryName } from '@/utils/contentTranslation';

interface Category {
  id: string;
  name: string;
  parent_id: string | null;
}

interface ProductSidebarProps {
  selectedCategory: string | null;
  onCategorySelect: (categoryId: string | null) => void;
}

const ProductSidebar = ({
  selectedCategory,
  onCategorySelect
}: ProductSidebarProps) => {
  const { t, currentLanguage } = useLanguage();
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchSubcategories(selectedCategory);
    } else {
      setSubcategories([]);
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .is('parent_id', null)
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async (parentId: string) => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('parent_id', parentId)
        .order('name');
      
      if (error) throw error;
      setSubcategories(data || []);
    } catch (error) {
      console.error('Error loading subcategories:', error);
    }
  };


  if (loading) {
    return (
      <div className="bg-brand-dark rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-700 rounded w-3/4"></div>
          <div className="h-4 bg-gray-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-brand-dark rounded-lg p-6 space-y-6">
      {/* Categorias */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">{t('categories.categoriesTitle')}</h3>
        <div className="space-y-2">
          <Button
            variant={!selectedCategory ? "default" : "ghost"}
            className="w-full justify-start text-left"
            onClick={() => onCategorySelect(null)}
          >
            {t('categories.allCategories')}
          </Button>
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? "default" : "ghost"}
              className="w-full justify-start text-left"
              onClick={() => onCategorySelect(category.id)}
            >
              {translateCategoryName(category.name, currentLanguage)}
            </Button>
          ))}
        </div>
      </div>

      {/* Subcategorias */}
      {subcategories.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">{t('categories.subcategories')}</h3>
          <div className="space-y-2">
            {subcategories.map((subcategory) => (
              <Button
                key={subcategory.id}
                variant="ghost"
                className="w-full justify-start text-left"
                onClick={() => onCategorySelect(subcategory.id)}
              >
                {translateCategoryName(subcategory.name, currentLanguage)}
              </Button>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default ProductSidebar;
